/** SafetySettings — review mode, Comment-to-DM, weekend activity (PUT /api/settings/safety). */

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import type { WorkflowSafetyConfig, ReviewMode } from '@dealcloser/shared'
import { DEFAULT_WORKFLOW_SAFETY } from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BetaBadge } from '@/components/beta-badge'
import { PlanGate } from '@/components/plan-gate'
import { useApi } from '@/hooks/use-api'
import { usePlan } from '@/hooks/use-plan'

const safetyKeys = { config: ['settings', 'safety'] as const }

type ToggleRowProps = {
  readonly id: string; readonly checked: boolean; readonly onToggle: () => void
  readonly disabled: boolean; readonly label: string; readonly desc: string
}

function ToggleRow({ id, checked, onToggle, disabled, label, desc }: ToggleRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onToggle} disabled={disabled} />
      <div>
        <label htmlFor={id} className="text-sm font-medium cursor-pointer">{label}</label>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  )
}

type RadioRowProps = {
  readonly id: string; readonly selected: boolean; readonly onSelect: () => void
  readonly disabled: boolean; readonly label: string; readonly desc: string
}

function RadioRow({ id, selected, onSelect, disabled, label, desc }: RadioRowProps) {
  return (
    <button
      type="button"
      id={id}
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
        selected ? 'border-cobalt-bright bg-primary/10' : 'border-border hover:bg-muted/40'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <span
        aria-hidden
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          selected ? 'border-cobalt-bright' : 'border-input'
        }`}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
    </button>
  )
}

export default function SafetySettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const plan = usePlan()
  const [form, setForm] = useState<WorkflowSafetyConfig>(DEFAULT_WORKFLOW_SAFETY)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: safetyKeys.config,
    queryFn: async (): Promise<WorkflowSafetyConfig> => {
      const res = await apiFetch<WorkflowSafetyConfig>('/api/settings/safety')
      if (!res.success || !res.data) return DEFAULT_WORKFLOW_SAFETY
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: WorkflowSafetyConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/safety', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: safetyKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  function toggleField(field: keyof WorkflowSafetyConfig) {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  function setReviewMode(mode: ReviewMode) {
    setForm((prev) => ({ ...prev, reviewMode: mode }))
  }

  const reviewMode: ReviewMode = form.reviewMode ?? 'none'

  return (
    <div className="space-y-6">
      {/* Manual Message Review */}
      <Card>
        <CardHeader>
          <CardTitle>Review Messages Manually</CardTitle>
          <CardDescription>
            Choose whether AI messages need your approval before they send.
            Held messages wait in your Inbox review queue and you get a
            notification for each one. Connection invites carry no text and
            always send automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2" role="radiogroup" aria-label="Review mode">
          <RadioRow
            id="review-mode-all"
            selected={reviewMode === 'all'}
            onSelect={() => setReviewMode('all')}
            disabled={isLoading || isSaving}
            label="Approve every message"
            desc="Nothing sends until you approve it. Every outreach message and AI reply waits for your OK."
          />
          <RadioRow
            id="review-mode-flagged"
            selected={reviewMode === 'flagged'}
            onSelect={() => setReviewMode('flagged')}
            disabled={isLoading || isSaving}
            label="Approve flagged messages only"
            desc="High scoring drafts send automatically. Drafts with a lower quality score wait for your approval. AI replies keep sending on their own."
          />
          <RadioRow
            id="review-mode-none"
            selected={reviewMode === 'none'}
            onSelect={() => setReviewMode('none')}
            disabled={isLoading || isSaving}
            label="No approvals needed"
            desc="Every message that passes quality checks sends on its own. This is the default."
          />
        </CardContent>
      </Card>

      {/* Only toggles the backend actually reads are shown (2026-09-26 QA
          finding). Removed: Engagement Warmup (not a feature, never
          advertise it), AI Reply Handler (replies are governed by the review
          mode above), a duplicate Content Poster toggle (the real one is in
          Settings > Content), and the daily caps / ramp-up / off-hours
          inputs (volume and hours are platform managed). The fields stay in
          the saved blob so nothing is lost. */}
      <PlanGate requiredPlan="pro" currentPlan={plan}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Comment-to-DM <BetaBadge /></CardTitle>
            <CardDescription>
              Message people who engage with your LinkedIn posts. Off by default.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ToggleRow id="safety-comment-dm" checked={form.commentDmEnabled}
              onToggle={() => toggleField('commentDmEnabled')} disabled={isLoading || isSaving}
              label="Send DMs to people who engage with my posts"
              desc="Posts without a keyword: people who match your ICP and like or comment get a short AI-written DM about what they engaged with. Posts with a comment keyword (set on the post in Content): only people who comment the keyword get your own message." />
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              <li>Only 1st-degree connections are messaged. LinkedIn does not allow DMs to anyone else, so those engagers are skipped.</li>
              <li>At most 10 a day and one per person per post, on weekdays 9 AM to 5 PM New York time (weekends only if weekend activity is on below).</li>
              <li>Never sent to anyone in a campaign, already messaged by a campaign, who said no, or whose conversation you took over.</li>
              <li>Your review mode above applies: with &quot;Approve every message&quot; each DM waits in your Inbox.</li>
            </ul>
          </CardContent>
        </Card>
      </PlanGate>

      {/* Activity schedule */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Schedule</CardTitle>
          <CardDescription>
            Daily volume and sending hours are managed by the platform to keep your LinkedIn
            account safe. Sends go out on weekdays inside each campaign&apos;s sending window
            (9:00 AM to 5:00 PM New York time unless the campaign sets its own).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ToggleRow id="safety-weekend" checked={form.weekendEnabled}
            onToggle={() => toggleField('weekendEnabled')} disabled={isLoading || isSaving}
            label="Continue activity on weekends" desc="If off, outreach and automated posting pause Saturday and Sunday. Replies to incoming messages keep working." />
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Safety Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
}
